import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LEVEL_META, ATTEND_STATUS, todayStr, dateLabel, waLink, currentMonth, monthLabel, shortDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar } from "@/components/PhotoUpload";
import { Loader2, MessageCircle, Repeat, CalendarX, BarChart3, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = ["hadir", "izin", "sakit", "alpa", "lainnya"];

function DailyTab() {
  const [data, setData] = useState(null);
  const [date, setDate] = useState(todayStr());

  const load = () => api.get("/sessions/day", { params: { date } }).then((r) => setData(r.data));
  useEffect(() => { setData(null); load(); }, [date]);

  const mark = async (s, status) => {
    setData((d) => ({ ...d, sessions: d.sessions.map((x) => x.student_id === s.student_id ? { ...x, attendance: { ...(x.attendance || {}), status } } : x) }));
    try {
      const { data: r } = await api.post("/attendance", { student_id: s.student_id, date, status, note: "" });
      if (r.wa_notif?.sent) toast.success(`${s.name}: ${ATTEND_STATUS[status].label} · WA terkirim ke wali ✅`);
      else if (r.wa_notif && r.wa_notif.reason !== "nonaktif") toast.warning(`${s.name}: ${ATTEND_STATUS[status].label} · WA gagal: ${r.wa_notif.reason}`);
      else toast.success(`${s.name}: ${ATTEND_STATUS[status].label}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-slate-500">{data ? `${data.day}, ${dateLabel(date)} · ${data.sessions.length} sesi terjadwal` : dateLabel(date)}</p>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Tanggal</label>
          <Input type="date" data-testid="attendance-date-input" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl border-amber-200 w-44" />
        </div>
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> :
        data.sessions.length === 0 ? (
          <Card data-testid="attendance-empty" className="p-12 text-center rounded-2xl border-amber-100 text-slate-400"><CalendarX className="w-10 h-10 mx-auto mb-2 text-slate-300" />Tidak ada jadwal pada tanggal ini.</Card>
        ) : (
          <div className="space-y-3">
            {data.sessions.map((s) => {
              const cur = s.attendance?.status;
              return (
                <Card key={`${s.kind}-${s.student_id}`} data-testid={`attendance-row-${s.student_id}`} className={`p-4 rounded-2xl ${s.kind === "pengganti" ? "border-violet-200 bg-violet-50/40" : "border-amber-100"}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <Avatar src={s.photo} name={s.name} size="w-10 h-10" rounded="rounded-xl" text="text-base" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{s.name} <span className="text-orange-600 font-bold ml-1">{s.time}</span></div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant="outline" className={`text-[10px] rounded-full ${LEVEL_META[s.level]?.cls}`}>{s.level}</Badge>
                        {s.kind === "pengganti" && <Badge data-testid={`pengganti-badge-${s.student_id}`} className="text-[10px] rounded-full border-0 bg-violet-100 text-violet-700"><Repeat className="w-3 h-3 mr-1" /> Pengganti dari {shortDate(s.original_date)}</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {OPTIONS.map((o) => (
                        <button key={o} data-testid={`mark-${o}-${s.student_id}`} onClick={() => mark(s, o)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${cur === o ? ATTEND_STATUS[o].cls + " ring-2 ring-offset-1" : "bg-white text-slate-500 border-slate-200 hover:border-orange-300"}`}>
                          {ATTEND_STATUS[o].label}
                        </button>
                      ))}
                      <Button size="sm" variant="ghost" onClick={() => window.open(waLink(s.wa, `Assalamu'alaikum Bapak/Ibu 🙏\n\nAnanda ${s.name} tercatat *${ATTEND_STATUS[cur || "hadir"].label}* pada les ${dateLabel(date)} di *Bimbel Gemma*. Terima kasih 💛`), "_blank")}
                        className="rounded-lg text-emerald-600 hover:bg-emerald-50 h-8 px-2"><MessageCircle className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}

function RecapTab() {
  const [month, setMonth] = useState(currentMonth());
  const [report, setReport] = useState(null);
  useEffect(() => { setReport(null); api.get("/reports/monthly", { params: { month } }).then((r) => setReport(r.data)); }, [month]);
  const pct = report?.summary.total_sesi ? Math.round((report.summary.hadir / report.summary.total_sesi) * 100) : 0;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-slate-500">Rekap kehadiran per siswa · {monthLabel(month)}</p>
        <div><label className="text-xs text-slate-400 block mb-1">Bulan</label><Input type="month" data-testid="recap-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl border-amber-200 w-44" /></div>
      </div>
      {!report ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Siswa Aktif", report.summary.students, ""], ["Total Sesi", report.summary.total_sesi, ""], ["Hadir", report.summary.hadir, "text-emerald-600"], ["% Kehadiran", `${pct}%`, "text-orange-600"]].map(([k, v, c]) => (
              <Card key={k} className="p-4 rounded-2xl border-amber-100"><div className="text-xs text-slate-400">{k}</div><div className={`font-display text-2xl font-bold ${c || "text-slate-900"}`}>{v}</div></Card>
            ))}
          </div>
          <Card className="rounded-2xl border-amber-100 overflow-hidden">
            <table className="w-full text-sm" data-testid="recap-table">
              <thead><tr className="bg-amber-50 text-slate-600 text-xs">
                <th className="py-2.5 px-3 text-left">Siswa</th><th className="py-2.5 px-2 text-center text-emerald-700">Hadir</th><th className="py-2.5 px-2 text-center text-sky-700">Izin</th><th className="py-2.5 px-2 text-center text-amber-700">Sakit</th><th className="py-2.5 px-2 text-center text-rose-700">Alpa</th><th className="py-2.5 px-3 text-left w-48">Kehadiran</th>
              </tr></thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.id} data-testid={`recap-row-${r.id}`} className="border-t border-amber-50">
                    <td className="py-2.5 px-3"><div className="font-semibold text-slate-800">{r.name}</div><div className="text-[11px] text-slate-400">{r.class_label || r.level}</div></td>
                    <td className="py-2.5 px-2 text-center font-semibold text-emerald-600">{r.hadir}</td><td className="py-2.5 px-2 text-center">{r.izin}</td><td className="py-2.5 px-2 text-center">{r.sakit}</td><td className="py-2.5 px-2 text-center text-rose-600">{r.alpa}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${r.attendance_pct}%` }} /></div>
                        <span className="text-xs font-bold text-slate-700 w-10 text-right">{r.total_sesi ? `${r.attendance_pct}%` : "-"}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

export default function Attendance() {
  const [tab, setTab] = useState("harian");
  return (
    <div className="space-y-6 animate-float-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Absensi</h1>
          <p className="text-slate-500 mt-1">Hanya siswa yang terjadwal pada tanggal terpilih · notifikasi WA otomatis saat tidak hadir</p>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl bg-amber-50">
            <TabsTrigger value="harian" data-testid="tab-harian" className="rounded-lg"><ClipboardCheck className="w-4 h-4 mr-1" /> Absensi Harian</TabsTrigger>
            <TabsTrigger value="rekap" data-testid="tab-rekap" className="rounded-lg"><BarChart3 className="w-4 h-4 mr-1" /> Rekap Bulanan</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {tab === "harian" ? <DailyTab /> : <RecapTab />}
    </div>
  );
}
